import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';
import Chip from '@components/chip/Chip';
import Icon from '@components/icon/Icon';
import deriveDarkColor, { getRandomTagColor } from '@helpers/color';

const FOCUS_OPEN_DEBOUNCE_MS = 200;

type CloseCallback = () => void;
const openInstances = new Set<CloseCallback>();
const closeAllExcept = (keep: CloseCallback) => {
  openInstances.forEach((cb) => {
    if (cb !== keep) cb();
  });
};

// Generic interfaces
export interface SearchableItem {
  id: string;
  name: string;
  color?: string;
}

export interface SearchableDropdownProps<T extends SearchableItem> {
  value: string;
  onChange: (value: string) => void;
  onItemsChange: (items: T[], type: 'add' | 'remove') => void;
  selectedItems: T[];
  availableItems?: T[];
  onUpdateItem?: (item: T) => void | boolean | Promise<void | boolean>;
  onCreateItem?: (name: string, color?: string) => T;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowCreate?: boolean;
  allowMultiple?: boolean;
  searchIcon?: React.ReactNode;
  hideChevron?: boolean;
  renderChip?: (
    item: T,
    onRemove: (evt: React.MouseEvent) => void
  ) => React.ReactNode;
  chipPosition?: 'top' | 'bottom';
  SelectorComponent?: React.ComponentType<any>;
  validateNameLength?: (name: string) => boolean;
  maxInputLength?: number;
  renderSelectedBelow?: boolean;
  selectorProps?: Record<string, any>;
  defaultTagColor?: string;
  showSelectionTooltip?: boolean;
  openOnFocus?: boolean;
  keepOpenOnSelect?: boolean;
  // false makes the input read-only: items can only be picked from the dropdown
  searchable?: boolean;
}

const SearchableDropdown = <T extends SearchableItem>({
  value,
  onChange,
  onItemsChange,
  selectedItems,
  availableItems = [],
  onUpdateItem,
  onCreateItem,
  placeholder = 'Search or select...',
  className = '',
  disabled = false,
  allowCreate = true,
  allowMultiple = true,
  renderChip,
  chipPosition = 'bottom',
  SelectorComponent,
  validateNameLength,
  maxInputLength,
  searchIcon,
  hideChevron,
  renderSelectedBelow = false,
  selectorProps,
  defaultTagColor = '#F2E1E9',
  showSelectionTooltip = false,
  openOnFocus = false,
  keepOpenOnSelect = false,
  searchable = true
}: SearchableDropdownProps<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(defaultTagColor);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasManualColorRef = useRef(false);
  const previousValueRef = useRef(value);
  const isColorControlled = selectorProps?.selectedColor != null;

  const handleColorChange = (color: string) => {
    hasManualColorRef.current = true;
    setSelectedColor(color);
  };

  useEffect(() => {
    const hadValue = previousValueRef.current.trim().length > 0;
    const hasValue = value.trim().length > 0;
    previousValueRef.current = value;

    if (!hasValue) {
      hasManualColorRef.current = false;
      return;
    }

    if (!hadValue && !hasManualColorRef.current && !isColorControlled) {
      setSelectedColor(getRandomTagColor());
    }
  }, [value, isColorControlled]);

  const closeSelf = useRef(() => setIsDropdownOpen(false));

  useEffect(() => {
    const cb = closeSelf.current;
    openInstances.add(cb);
    return () => {
      openInstances.delete(cb);
    };
  }, []);

  const openDropdown = () => {
    closeAllExcept(closeSelf.current);
    setIsDropdownOpen(true);
  };

  const toggleIntentRef = useRef(false);
  const focusOpenedAtRef = useRef(0);

  const handleInputClick = () => {
    if (!disabled) {
      const justFocusOpened =
        Date.now() - focusOpenedAtRef.current < FOCUS_OPEN_DEBOUNCE_MS;
      if (openOnFocus && isDropdownOpen && !justFocusOpened) {
        toggleIntentRef.current = true;
        setIsDropdownOpen(false);
        return;
      }
      inputRef.current?.focus();
      if (openOnFocus && !isDropdownOpen) {
        openDropdown();
      }
    }
  };

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      if (isDropdownOpen) {
        toggleIntentRef.current = true;
        setIsDropdownOpen(false);
      } else {
        openDropdown();
        inputRef.current?.focus();
      }
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    setIsSearching(false);
    if (openOnFocus) {
      if (toggleIntentRef.current) {
        toggleIntentRef.current = false;
        return;
      }
      focusOpenedAtRef.current = Date.now();
      openDropdown();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange(next);
    setIsSearching(next.trim().length > 0);
    if (openOnFocus || next.trim().length > 0) {
      openDropdown();
    } else {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isInputFocused && value.trim().length > 0) {
      setIsDropdownOpen(true);
    }
  }, [value, isInputFocused]);

  // Close dropdown on scroll/resize
  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const close = () => setIsDropdownOpen(false);
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const isInsideDropdown =
        containerRef.current && containerRef.current.contains(target);
      if (!isInsideDropdown) close();
    };
    window.addEventListener('resize', close);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('resize', close);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isDropdownOpen]);

  const handleItemSelect = (item: T) => {
    if (allowMultiple) {
      if (!selectedItems.some((selected) => selected.id === item.id)) {
        onItemsChange([...selectedItems, item], 'add');
      } else {
        onItemsChange(selectedItems, 'add');
      }
      // Clear the typed query so the next search starts fresh.
      onChange('');
    } else {
      onItemsChange([item], 'add');
    }
    setIsSearching(false);
    if (!keepOpenOnSelect) setIsDropdownOpen(false);
  };

  const handleCreateItem = (itemName: string, color?: string) => {
    if (!onCreateItem) return;

    if (validateNameLength && !validateNameLength(itemName)) {
      return;
    }

    const newItem = onCreateItem(itemName, color ?? selectedColor);

    if (allowMultiple) {
      if (
        !selectedItems.some(
          (selected) =>
            (selected.name?.toLowerCase() || '') ===
            (itemName?.toLowerCase() || '')
        )
      ) {
        onItemsChange([...selectedItems, newItem], 'add');
      }
    } else {
      onItemsChange([newItem], 'add');
      if (!keepOpenOnSelect) setIsDropdownOpen(false);
    }

    onChange('');
    if (!keepOpenOnSelect) setIsDropdownOpen(false);
  };

  const handleUpdateItem = (newItem: T) =>
    newItem.id && onUpdateItem ? onUpdateItem(newItem) : undefined;

  const handleRemoveItem = (itemToRemove: T, evt: React.MouseEvent) => {
    evt.stopPropagation();
    onItemsChange(
      selectedItems.filter((item) => item.id !== itemToRemove.id),
      'remove'
    );
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      // If there's a search value and allowCreate is true, create a new item
      const trimmed = value.trim();
      const existingItem = availableItems.find(
        (item) => (item.name?.toLowerCase() || '') === trimmed.toLowerCase()
      );

      if (existingItem) {
        handleItemSelect(existingItem);
        return;
      }

      if (allowCreate && onCreateItem) {
        // Use the selected color for Enter key creation
        handleCreateItem(trimmed, selectedColor);
      }
    }
  };

  const defaultRenderChip = (
    item: T,
    onRemove: (e: React.MouseEvent) => void
  ) => (
    <Chip
      key={item.id}
      label={item.name}
      className="px-2 py-1 text-sm"
      style={{
        backgroundColor: item.color || '#FFFFFF',
        color: deriveDarkColor(item.color || '#FFFFFF')
      }}
      labelStyle="!text-sm !font-normal"
      size="16"
      onRemove={onRemove}
    />
  );

  const validSelectedItems = selectedItems.filter((item) => item.id);

  const showChip =
    allowMultiple && renderSelectedBelow && validSelectedItems.length > 0;

  const getInputComponent = () => {
    if (allowMultiple) {
      return (
        <div
          className={clsx(
            'flex flex-1 items-center gap-2',
            !renderSelectedBelow && 'flex-wrap'
          )}
        >
          {searchIcon}
          {!renderSelectedBelow &&
            [...validSelectedItems]
              .reverse()
              .map((item) =>
                renderChip
                  ? renderChip(item, (evt) => handleRemoveItem(item, evt))
                  : defaultRenderChip(item, (evt) =>
                      handleRemoveItem(item, evt)
                    )
              )}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            onBlur={() => setIsInputFocused(false)}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxInputLength}
            className={clsx(
              'min-w-30 flex-1 bg-transparent text-sm text-darkGrey outline-none placeholder:!text-base placeholder:!font-normal placeholder:text-grey',
              !searchable && 'cursor-pointer'
            )}
            readOnly={!searchable}
          />
        </div>
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        onFocus={handleInputFocus}
        onBlur={() => setIsInputFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxInputLength}
        className={clsx(
          'min-w-30 flex-1 bg-transparent text-sm text-darkGrey outline-none placeholder:!text-base placeholder:!font-normal placeholder:text-grey disabled:text-cofeeGreyBlue6',
          !searchable && 'cursor-pointer'
        )}
        readOnly={!searchable}
      />
    );
  };

  return (
    <div
      ref={containerRef}
      data-searchable-dropdown
      className={clsx('relative', className)}
    >
      {/* Selected tags above input (scoped via prop) */}
      {chipPosition === 'top' && showChip && (
        <div className="m-2 flex flex-wrap items-center gap-2">
          {validSelectedItems.map((item) =>
            renderChip
              ? renderChip(item, (evt) => handleRemoveItem(item, evt))
              : defaultRenderChip(item, (evt) => handleRemoveItem(item, evt))
          )}
        </div>
      )}

      {/* Input Container */}
      <Button
        variant="tertiary"
        handleClick={handleInputClick}
        size="medium"
        className={clsx(
          '!h-auto !max-h-fit w-full min-w-166 justify-between border border-cofeeNeutralInactive',
          'flex min-h-12 cursor-pointer items-center gap-2',
          'px-4 py-3',
          'focus-within:border-primary',
          disabled ? 'cursor-not-allowed !bg-grey10' : '!bg-theme'
        )}
        disabled={disabled}
      >
        {getInputComponent()}
        {!hideChevron && (
          <div
            role="button"
            tabIndex={-1}
            aria-expanded={isDropdownOpen}
            aria-label="Toggle dropdown"
            onClick={handleChevronClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleChevronClick(e as any);
            }}
            className="flex items-center"
          >
            <Icon
              name="down"
              className={clsx(
                'size-4 text-grey transition-transform',
                isDropdownOpen && 'rotate-180'
              )}
            />
          </div>
        )}
      </Button>

      {/* Selected tags below input (scoped via prop) */}
      {chipPosition === 'bottom' && showChip && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {validSelectedItems.map((item) =>
            renderChip
              ? renderChip(item, (evt) => handleRemoveItem(item, evt))
              : defaultRenderChip(item, (evt) => handleRemoveItem(item, evt))
          )}
        </div>
      )}

      {/* Render the selector component if provided */}
      {SelectorComponent && (
        <SelectorComponent
          isOpen={isDropdownOpen}
          onClose={() => setIsDropdownOpen(false)}
          onSelectItem={handleItemSelect}
          onCreateItem={allowCreate ? handleCreateItem : undefined}
          onUpdateItem={handleUpdateItem}
          selectedItems={selectedItems}
          searchValue={isSearching ? value : ''}
          itemOptions={availableItems}
          className={selectorProps?.positionFixed ? undefined : 'top-full'}
          selectedColor={selectedColor}
          onColorChange={handleColorChange}
          positionFixed={selectorProps?.positionFixed}
          getAnchorRect={() => {
            const root = containerRef.current;
            if (!root) return null;
            const buttonEl = root.querySelector('button');
            if (buttonEl && buttonEl instanceof HTMLElement) {
              return buttonEl.getBoundingClientRect();
            }
            return root.getBoundingClientRect();
          }}
          showSelectionTooltip={showSelectionTooltip}
          {...selectorProps}
        />
      )}
    </div>
  );
};

export default SearchableDropdown;
